// apps/web/src/stores/toast.ts
// Per. In-house toast stack. Auto-dismiss via setTimeout:
// 4s for info/success, 7s for error. Stackable up to 3 (enforced in UI).
import { create } from 'zustand';

export type ToastTone = 'info' | 'success' | 'error';

export interface Toast {
  id: string;
  tone: ToastTone;
  text: string;
}

export interface ToastInput {
  tone: ToastTone;
  text: string;
}

interface ToastState {
  toasts: Toast[];
  show: (t: ToastInput) => string;
  dismiss: (id: string) => void;
}

function makeId(): string {
  // Non-crypto identity for in-memory UI state. Monotonic + random keeps it
  // unique even under the same-millisecond show-in-a-loop pattern.
  return `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function toneDelayMs(tone: ToastTone): number {
  return tone === 'error' ? 7000 : 4000;
}

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],
  show: ({ tone, text }) => {
    const id = makeId();
    const toast: Toast = { id, tone, text };
    set({ toasts: [...get().toasts, toast] });
    if (typeof setTimeout !== 'undefined') {
      setTimeout(() => get().dismiss(id), toneDelayMs(tone));
    }
    return id;
  },
  dismiss: (id) => {
    set({ toasts: get().toasts.filter((t) => t.id !== id) });
  },
}));
